import type { StoreApplyRequest, StoreCandidate } from '@mcgames/contracts';
import { Badge, Button, Spinner, TextInput } from '@mcgames/ui';
import { useId, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useApiQuery } from '../../api/hooks.ts';
import { ErrorText } from './ErrorText.tsx';
import styles from './Games.module.css';

export type CandidatePickerProps = {
  /** The store entries the import found; the free search lists more. */
  candidates: StoreCandidate[];
  /** Prefills the search field. */
  initialQuery: string;
  busy: boolean;
  /** The error of whatever `onPick` started. */
  error: unknown;
  onPick: (choice: StoreApplyRequest) => void;
};

/** Steam and GOG entries to identify an import with: the found candidates and a free search. */
export function CandidatePicker({
  candidates,
  initialQuery,
  busy,
  error,
  onPick,
}: CandidatePickerProps) {
  const { t } = useTranslation('admin');
  const headingId = useId();
  const [query, setQuery] = useState(initialQuery);
  const [submitted, setSubmitted] = useState('');
  const results = useApiQuery<StoreCandidate[]>(
    `/api/admin/metadata/store/search?q=${encodeURIComponent(submitted)}`,
    { enabled: submitted !== '' },
  );

  function search(event: FormEvent) {
    event.preventDefault();
    setSubmitted(query.trim());
  }

  const shown = submitted !== '' && results.data !== undefined ? results.data : candidates;

  return (
    <div className={styles['candidates']}>
      <h3 id={headingId} className={styles['subheading']}>
        {t('games.import.candidates')}
      </h3>
      <form className={styles['row']} onSubmit={search}>
        <div className={styles['grow']}>
          <TextInput
            label={t('games.import.searchLabel')}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
        <Button type="submit" disabled={query.trim() === ''}>
          {t('games.import.search')}
        </Button>
      </form>
      {results.isFetching && <Spinner />}
      <ErrorText error={results.error ?? error} />
      {submitted !== '' && results.data?.length === 0 && (
        <p className={styles['muted']}>{t('games.import.noResults')}</p>
      )}
      {shown.length > 0 && (
        <ul className={styles['candidateList']} aria-labelledby={headingId}>
          {shown.map((candidate) => (
            <li key={`${candidate.source}:${candidate.id}`} className={styles['candidate']}>
              {candidate.coverUrl !== null ? (
                <img className={styles['candidateCover']} src={candidate.coverUrl} alt="" />
              ) : (
                <span className={styles['candidateCover']} aria-hidden="true" />
              )}
              <div className={styles['candidateText']}>
                <span className={styles['candidateName']}>{candidate.name}</span>
                <span className={styles['hint']}>
                  {candidate.year !== null ? String(candidate.year) : t('games.import.noYear')}
                </span>
              </div>
              <Badge>{t(`games.import.source.${candidate.source}`)}</Badge>
              <Button
                size="sm"
                aria-label={t('games.import.useThisNamed', { name: candidate.name })}
                disabled={busy}
                onClick={() =>
                  onPick({
                    source: candidate.source,
                    id: candidate.id,
                    sgdbId: candidate.sgdbId,
                  })
                }
              >
                {t('games.import.useThis')}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
